"use client";

import { Upload } from "lucide-react";
import { useTranslations } from "next-intl";
import { Button, Checkbox } from "@artha/ui";
import type { UploadStepProps } from "../../personalization.types";
import { TALENT_NETWORK_UPLOAD_COPY } from "../../personalization.constants";

export function UploadStep({
  selectedFile,
  isUploading,
  hasUploadConsent,
  onUploadConsentChange,
  onChooseFile,
  onNext,
  onSignIn,
  nextLabel,
  showSignIn = true,
  isTalentNetwork = false,
}: UploadStepProps) {
  const t = useTranslations("profile.resumePersonalization");

  return (
    <div className="space-y-4">
      <button
        type="button"
        className="flex min-h-48 w-full flex-col items-center justify-center gap-3 rounded-[14px] border border-dashed border-black bg-white px-6 py-8 text-center"
        onClick={onChooseFile}
      >
        <Upload className="h-7 w-7 text-black" aria-hidden />
        <span className="text-sm font-semibold text-[#344054]">
          {selectedFile ? selectedFile.name : t("upload.selectFile")}
        </span>
        <span className="text-xs text-[#667085]">
          {t("upload.supportedFiles")}
        </span>
      </button>
      <Checkbox
        checked={hasUploadConsent}
        onChange={(event) => onUploadConsentChange(event.target.checked)}
        disabled={isUploading}
        aria-label={isTalentNetwork ? TALENT_NETWORK_UPLOAD_COPY.consent : undefined}
        label={isTalentNetwork ? (
          <span className="text-xs font-normal leading-5 text-[#667085]">
            I agree to artha.link&apos;s{" "}
            <a href={TALENT_NETWORK_UPLOAD_COPY.termsUrl} target="_blank" rel="noreferrer" className="underline hover:text-[#344054]">Terms of Service</a>{" "}
            and{" "}
            <a href={TALENT_NETWORK_UPLOAD_COPY.privacyUrl} target="_blank" rel="noreferrer" className="underline hover:text-[#344054]">Privacy Policy</a>.
          </span>
        ) : <span className="text-sm leading-5">{t("upload.consent")}</span>}
      />
      <Button
        type="button"
        className="h-12 w-full rounded-[10px] bg-primary font-bold text-primary-foreground"
        disabled={!selectedFile || !hasUploadConsent || isUploading}
        onClick={onNext}
      >
        {nextLabel || t("actions.next")}
      </Button>
      {showSignIn ? (
        <button
          type="button"
          className="w-full text-center text-sm font-semibold underline"
          onClick={onSignIn}
        >
          {isTalentNetwork ? TALENT_NETWORK_UPLOAD_COPY.signIn : t("upload.signIn")}
        </button>
      ) : null}
    </div>
  );
}
